import { hc } from "hono/client";
import { useEffect, useRef, useState } from "hono/jsx";
import type { AppType } from "@/app.tsx";
import { CodeBlock } from "./CodeBlock.tsx";

// @ts-ignore: Client type inference issues
const client = hc<AppType>("/") as any;

interface TimeMetrics {
  clientStartTime: string;
  clientEndTime: string;
  serverTime: string;
  rtt: number;
  offset: number;
}

interface BatchStats {
  count: number;
  rtt: {
    min: number;
    max: number;
    mean: number;
    median: number;
    stdDev: number;
  };
  offset: {
    min: number;
    max: number;
    mean: number;
    median: number;
    stdDev: number;
  };
  recommendedOffset: number;
}

const calculateStats = (values: number[]) => {
  if (values.length === 0) {
    return { min: 0, max: 0, mean: 0, median: 0, stdDev: 0 };
  }
  const sorted = [...values].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const mid = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
  const variance = values.reduce((a, b) => a + Math.pow(b - mean, 2), 0) /
    values.length;
  const stdDev = Math.sqrt(variance);
  return { min, max, mean, median, stdDev };
};

const LiveClock = ({ offset, label }: { offset: number; label: string }) => {
  const [time, setTime] = useState(Date.now() + offset);
  const [flashClass, setFlashClass] = useState<string>("");
  const animationFrameRef = useRef<number>(0);
  const lastSecondRef = useRef(Math.floor((Date.now() + offset) / 1000));

  useEffect(() => {
    const update = () => {
      const now = Date.now() + offset;
      setTime(now);

      const currentSecond = Math.floor(now / 1000);
      if (currentSecond !== lastSecondRef.current) {
        lastSecondRef.current = currentSecond;

        // Cycle: Error (Red) -> Info (Blue) -> Success (Green)
        const cycle = currentSecond % 3;

        // Use DaisyUI utility classes with opacity modifier
        const cls = cycle === 0
          ? "bg-error/20"
          : cycle === 1
          ? "bg-info/20"
          : "bg-success/20";

        setFlashClass(cls);
        setTimeout(() => setFlashClass(""), 150);
      }
      animationFrameRef.current = requestAnimationFrame(update);
    };

    animationFrameRef.current = requestAnimationFrame(update);
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [offset]);

  const date = new Date(time);

  return (
    <div
      class={`flex flex-col items-center p-2 rounded-lg shadow-inner bg-base-300 transition-colors duration-800 ${
        flashClass || ""
      }`}
      style={{ transitionDuration: flashClass ? "0ms" : "800ms" }}
    >
      <div class="text-xs uppercase font-bold opacity-50 mb-1">{label}</div>
      <div class="font-mono text-xl font-black tabular-nums tracking-wider text-accent-content">
        {date.toLocaleTimeString([], { hour12: false })}
        <span class="text-xs opacity-50 ml-1">
          .{date.getMilliseconds().toString().padStart(3, "0")}
        </span>
      </div>
    </div>
  );
};

export const ServerTime = () => {
  const [metrics, setMetrics] = useState<TimeMetrics | null>(null);
  const [loadingType, setLoadingType] = useState<"http" | "ws" | null>(null);
  const [batchMode, setBatchMode] = useState(false);
  const [stats, setStats] = useState<BatchStats | null>(null);

  const fetchHttpSync = async () => {
    const t1 = Date.now();
    const response = await client.api.clock.$get();
    const { time: serverTimeString } = await response.json();
    const t2 = Date.now();

    const serverTime = new Date(serverTimeString).getTime();
    const rtt = t2 - t1;
    const offset = serverTime - (t1 + t2) / 2;

    return {
      clientStartTime: new Date(t1).toISOString(),
      clientEndTime: new Date(t2).toISOString(),
      serverTime: serverTimeString,
      rtt,
      offset,
    };
  };

  const fetchWsSync = (): Promise<TimeMetrics> => {
    return new Promise((resolve, reject) => {
      const protocol = globalThis.location.protocol === "https:"
        ? "wss:"
        : "ws:";
      const ws = new WebSocket(
        `${protocol}//${globalThis.location.host}/ws/clock`,
      );

      ws.onopen = () => {
        const t1 = Date.now();
        ws.send(JSON.stringify({ type: "PING" }));

        ws.onmessage = (e) => {
          const data = JSON.parse(e.data);
          if (data.type === "PONG") {
            const t2 = Date.now();
            const serverTime = new Date(data.serverTime).getTime();
            const rtt = t2 - t1;
            const offset = serverTime - (t1 + t2) / 2;
            ws.close();
            resolve({
              clientStartTime: new Date(t1).toISOString(),
              clientEndTime: new Date(t2).toISOString(),
              serverTime: data.serverTime,
              rtt,
              offset,
            });
          }
        };
      };
      ws.onerror = (e) => reject(e);
    });
  };

  const runSync = async (type: "http" | "ws") => {
    setLoadingType(type);
    setMetrics(null);
    setStats(null);

    try {
      if (batchMode) {
        const runCount = 100;
        const rtts: number[] = [];
        const offsets: number[] = [];

        for (let i = 0; i < runCount; i++) {
          const m = type === "http"
            ? await fetchHttpSync()
            : await fetchWsSync();
          rtts.push(m.rtt);
          offsets.push(m.offset);
          if (type === "http") await new Promise((r) => setTimeout(r, 10));
        }

        const rttStats = calculateStats(rtts);
        const offsetStats = calculateStats(offsets);

        setStats({
          count: runCount,
          rtt: rttStats,
          offset: offsetStats,
          recommendedOffset: offsetStats.median,
        });

        setMetrics({
          clientStartTime: "",
          clientEndTime: "",
          serverTime: "",
          rtt: rttStats.mean,
          offset: offsetStats.median,
        });
      } else {
        const m = type === "http" ? await fetchHttpSync() : await fetchWsSync();
        setMetrics(m);
      }
    } catch (e) {
      console.error("Sync failed", e);
    } finally {
      setLoadingType(null);
    }
  };

  const renderStatGroup = (
    title: string,
    desc: string,
    data: {
      min: number;
      max: number;
      mean: number;
      median: number;
      stdDev: number;
    },
    tooltips: { min: string; max: string; median: string; stdDev: string },
  ) => (
    <div class="bg-base-200 rounded-lg p-3 mb-2">
      <div class="flex justify-between items-baseline mb-2">
        <h4 class="font-bold text-sm uppercase opacity-70">{title}</h4>
        <span class="text-xs opacity-50">{desc}</span>
      </div>
      <div class="grid grid-cols-4 gap-2 text-center text-xs">
        <div class="tooltip" data-tip={tooltips.min}>
          <div class="flex flex-col bg-base-100 p-1 rounded cursor-help transition-colors hover:bg-base-300">
            <span class="opacity-50 text-[10px] border-b border-dotted border-base-content/20 w-fit update mx-auto mb-1">
              Min
            </span>
            <span class="font-mono">{data.min.toFixed(1)}</span>
          </div>
        </div>
        <div class="tooltip" data-tip={tooltips.max}>
          <div class="flex flex-col bg-base-100 p-1 rounded cursor-help transition-colors hover:bg-base-300">
            <span class="opacity-50 text-[10px] border-b border-dotted border-base-content/20 w-fit mx-auto mb-1">
              Max
            </span>
            <span class="font-mono">{data.max.toFixed(1)}</span>
          </div>
        </div>
        <div class="tooltip" data-tip={tooltips.median}>
          <div class="flex flex-col bg-base-100 p-1 rounded cursor-help transition-colors hover:bg-base-300">
            <span class="opacity-50 text-[10px] border-b border-dotted border-base-content/20 w-fit mx-auto mb-1">
              Median
            </span>
            <span class="font-mono">{data.median.toFixed(1)}</span>
          </div>
        </div>
        <div class="tooltip" data-tip={tooltips.stdDev}>
          <div class="flex flex-col bg-base-100 p-1 rounded cursor-help transition-colors hover:bg-base-300">
            <span class="opacity-50 text-[10px] border-b border-dotted border-base-content/20 w-fit mx-auto mb-1">
              StdDev
            </span>
            <span class="font-mono text-accent">{data.stdDev.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div class="flex flex-col items-center gap-4">
      <div class="form-control">
        <label class="label cursor-pointer gap-2">
          <span class="label-text font-bold uppercase text-xs">
            Batch Mode (100x)
          </span>
          <input
            type="checkbox"
            class="toggle toggle-sm toggle-accent"
            checked={batchMode}
            onChange={(e) =>
              setBatchMode((e.target as HTMLInputElement).checked)}
          />
        </label>
      </div>

      <div class="join">
        <button
          type="button"
          class={`btn btn-secondary join-item font-black uppercase ${
            loadingType === "http" ? "loading" : ""
          }`}
          onClick={() => runSync("http")}
          disabled={!!loadingType}
        >
          {loadingType === "http" ? "SYNCING..." : "HTTP SYNC"}
        </button>
        <button
          type="button"
          class={`btn btn-primary join-item font-black uppercase ${
            loadingType === "ws" ? "loading" : ""
          }`}
          onClick={() => runSync("ws")}
          disabled={!!loadingType}
        >
          {loadingType === "ws" ? "SYNCING..." : "WS SYNC"}
        </button>
      </div>

      {metrics && (
        <div class="mt-4 flex flex-col items-center">
          <div class="flex gap-2 justify-center">
            {[
              { offset: 0, label: "Client Time" },
              {
                offset: stats ? stats.recommendedOffset : metrics.offset,
                label: "Server Time",
              },
            ].sort((a, b) => b.offset - a.offset).map((c) => (
              <LiveClock offset={c.offset} label={c.label} />
            ))}
          </div>
          <div
            class="tooltip"
            data-tip="Use this standardized time to trigger game events simultaneously across all clients (e.g. 'Match starts at 12:00:00.000')."
          >
            <div class="badge badge-xs badge-ghost cursor-help mt-1">
              Why use this?
            </div>
          </div>
        </div>
      )}

      {stats && (
        <div class="w-full max-w-sm mt-4 text-left">
          <div class="stats shadow w-full mb-4">
            <div class="stat">
              <div class="stat-title font-bold text-accent">
                Recommended Offset
              </div>
              <div class="stat-value text-2xl">
                {stats.recommendedOffset > 0 ? "+" : ""}
                {stats.recommendedOffset.toFixed(2)}ms
              </div>
              <div class="stat-desc">Based on {stats.count} runs (Median)</div>
            </div>
          </div>

          {renderStatGroup(
            "Round Trip (RTT)",
            "Network Latency",
            stats.rtt,
            {
              min:
                "Fastest possible response. The physical limit of your connection.",
              max:
                "Worst lag spike observed. Your prediction code must handle this case to avoid snapping!",
              median:
                "Typical latency. Tune your game feel (e.g. projectile speed) for this value.",
              stdDev:
                "Jitter/Instability. High values mean 'rubber-banding' is likely.",
            },
          )}
          {renderStatGroup(
            "Clock Offset",
            "Server vs Client Time",
            stats.offset,
            {
              min: "Lowest estimated difference.",
              max: "Highest estimated difference.",
              median:
                "Most reliable sync value. Use this to align game clocks.",
              stdDev:
                "Sync confidence. Lower means we are very sure about the Offset.",
            },
          )}
        </div>
      )}

      {metrics && !stats && (
        <div class="stats stats-vertical shadow border border-base-300 bg-base-100 w-full max-w-sm">
          <div class="stat">
            <div class="stat-title font-bold">Round Trip Time (RTT)</div>
            <div class="stat-value text-accent text-3xl font-mono">
              {metrics.rtt}ms
            </div>
            <div class="stat-desc">Latency is {metrics.rtt / 2}ms</div>
          </div>

          <div class="stat">
            <div class="stat-title font-bold">Clock Offset</div>
            <div
              class={`stat-value text-3xl font-mono ${
                Math.abs(metrics.offset) > 100 ? "text-error" : "text-success"
              }`}
            >
              {metrics.offset > 0 ? "+" : ""}
              {metrics.offset.toFixed(1)}ms
            </div>
            <div class="stat-desc">Client vs Server skew</div>
          </div>

          {metrics.clientStartTime && (
            <div class="stat text-xs opacity-70 font-mono">
              <div>Client T1: {metrics.clientStartTime.split("T")[1]}</div>
              <div>Server T: {metrics.serverTime.split("T")[1]}</div>
              <div>Client T2: {metrics.clientEndTime.split("T")[1]}</div>
            </div>
          )}
        </div>
      )}

      {metrics && (
        <button
          type="button"
          class="btn btn-outline btn-sm font-bold uppercase tracking-widest mt-4"
          onClick={() => {
            const skew = Math.abs(metrics.offset);
            // Map skew 0-500ms to Green-Red
            // 0ms = 120 (Green)
            // 500ms = 0 (Red)
            // HSL
            const hue = Math.max(0, 120 - (skew / 500 * 120));
            const color = `hsl(${hue}, 100%, 50%)`;

            const transition = document.documentElement.style.transition;

            // Lag Simulation: Wait for 'skew' ms before flashing
            setTimeout(() => {
              document.documentElement.style.transition = "none";
              document.documentElement.style.backgroundColor = color;

              setTimeout(() => {
                document.documentElement.style.transition =
                  "background-color 0.5s ease-out";
                document.documentElement.style.backgroundColor = "";

                setTimeout(() => {
                  document.documentElement.style.transition = transition;
                }, 500);
              }, 100);
            }, skew);
          }}
        >
          Simulate server clock {metrics.offset > 0 ? "lead" : "lag"}
        </button>
      )}

      <div class="card bg-base-100 shadow-sm border border-base-300 w-full max-w-sm mt-4">
        <div class="card-body p-4">
          <h3 class="font-bold text-sm uppercase opacity-50 mb-2">
            How it works
          </h3>

          <div class="text-xs opacity-70 space-y-2 mt-2">
            <p>
              <span class="font-bold text-primary">Offset</span> ={" "}
              <span class="text-accent">ServerTime</span>{" "}
              - (<span class="text-primary">T1</span> +{" "}
              <span class="text-secondary">T2</span>)/2
            </p>
            <div class="alert alert-warning text-xs p-2 mt-2 leading-tight">
              <span>
                Assumes <b>Symmetric Latency</b>{" "}
                (Request time = Response time). In real networks, asymmetry adds
                error to the offset.
              </span>
            </div>

            <div class="divider my-1"></div>

            <div class="grid grid-cols-2 gap-2">
              <div class="flex flex-col items-center">
                <span class="opacity-50 text-[10px]">Animation Speed</span>
                <div
                  class="tooltip"
                  data-tip="Syncing animation to your specific Round Trip Time. Slower animation = Slower connection."
                >
                  <span class="badge badge-outline font-mono font-bold cursor-help text-xs">
                    {metrics?.rtt?.toFixed(0) || "1000"}ms RTT
                  </span>
                </div>
              </div>
              <div class="flex flex-col items-center">
                <span class="opacity-50 text-[10px]">Feedback Loop</span>
                <div
                  class="tooltip"
                  data-tip="Max frequency of confirmed round-trips. Your inputs can't be confirmed faster than this rate."
                >
                  <span class="badge badge-ghost font-mono font-bold cursor-help text-xs">
                    {metrics?.rtt
                      ? Math.floor(1000 / Math.max(1, metrics.rtt))
                      : "1"} Hz
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reference Implementation */}
      <div class="collapse collapse-arrow w-full max-w-sm mt-4">
        <input type="checkbox" />
        <div class="collapse-title text-sm font-bold opacity-70 uppercase">
          Get the Code
        </div>
        <div class="collapse-content text-left p-0">
          <p class="text-xs opacity-70 mb-2">
            A robust, copy-pasteable TypeScript class for your game.
          </p>
          <CodeBlock
            language="typescript"
            code={`export class TimeSync {
  offset = 0;
  rtt = 0;

  async sync() {
    const t1 = Date.now();
    const serverTime = await fetchTime();
    const t2 = Date.now();
    this.rtt = t2 - t1;
    this.offset = serverTime - (t1 + this.rtt/2);
  }

  async syncBatch(count = 10) {
    const offsets = [];
    for(let i=0; i<count; i++) {
      await this.sync();
      offsets.push(this.offset);
    }
    offsets.sort((a,b) => a-b);
    const mid = Math.floor(offsets.length/2);
    this.offset = offsets.length % 2 !== 0 
      ? offsets[mid]
      : (offsets[mid-1] + offsets[mid]) / 2; 
  }

  now() {
    return Date.now() + this.offset;
  }
}`}
          />
        </div>
      </div>

      <div class="card bg-base-100 shadow-sm border border-base-300 w-full max-w-sm mt-4">
        <div class="card-body p-4">
          <h3 class="font-bold text-sm uppercase opacity-50 mb-2">
            Game Dev Implications
          </h3>

          <div class="space-y-3 text-xs">
            <div>
              <h4 class="font-bold text-primary">Interpolation Delay</h4>
              <p class="opacity-70">
                Don't render at exactly{" "}
                <span class="font-mono text-[10px] bg-base-200 px-1 rounded">
                  ServerTime
                </span>. Render at{" "}
                <span class="font-mono text-[10px] bg-base-200 px-1 rounded">
                  ServerTime - 100ms
                </span>{" "}
                (or 2-3 tick duration) to smooth out jitter and prevent snapping
                when packets arrive late.
              </p>
            </div>

            <div>
              <h4 class="font-bold text-primary">measure vs use</h4>
              <p class="opacity-70">
                <b>Metrics</b> (this tool) measure the network.
                <b>Game Clock</b>{" "}
                should be a smooth, monotonically increasing value that{" "}
                <i>chases</i>{" "}
                the measured offset over time, avoiding sudden jumps.
              </p>
            </div>

            <div>
              <h4 class="font-bold text-primary">Clock Drift</h4>
              <p class="opacity-70">
                Hardware clocks vary (drift). You must re-sync periodically
                (e.g. every 30s) or continuously adjust your game speed (e.g.
                run at 1.01x speed) to stay aligned without snapping.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
